import dayjs from 'dayjs';
import { normalizeFileName } from '../../../components/ui/utils/fileNameEncoding';

/**
 * Export complaint report to PDF
 * Clean, professional design matching form structure
 */
export const exportComplaintToPdf = async (complaint, includeAttachments = false) => {
  let missingFilesCount = 0;
  try {
    // Debug: Log the complaint object structure when it arrives
    console.log('📄 PDF Export - Complaint object received:', {
      id: complaint?.id,
      code: complaint?.code,
      hasApprovalRequests: !!complaint?.approvalRequests,
      approvalRequestsType: typeof complaint?.approvalRequests,
      approvalRequestsIsArray: Array.isArray(complaint?.approvalRequests),
      approvalRequestsLength: complaint?.approvalRequests?.length,
      approvalRequestsData: complaint?.approvalRequests
    });
    
    // Dynamic imports to avoid circular dependencies
    const pdfMake = await import('pdfmake/build/pdfmake');
    const pdfFonts = await import('pdfmake/build/vfs_fonts');
    const { complaintConfig } = await import('../complaintConfig');
    const { API_BASE_URL, API_ENDPOINTS } = await import('../../../config/apiConfig');
    
    pdfMake.default.vfs = pdfFonts.vfs || pdfFonts.default || {};

    // Define logo image key for pdfMake
    const logoImageKey = 'companyLogo';

    // Fetch company logo from application settings
    let companyLogoBase64 = null;
    try {
      const token = localStorage.getItem('authToken');
      const logoSettingResponse = await fetch(`${API_BASE_URL}/application-setting/key/companyLogo`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      if (logoSettingResponse.ok) {
        const logoSetting = await logoSettingResponse.json();
        if (logoSetting && logoSetting.value && logoSetting.isActive !== false) {
          let logoUrl = logoSetting.value.trim();
          
          // Construct full URL if not already absolute
          if (!logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
            const baseUrl = API_BASE_URL.replace('/api', '').replace(/\/$/, '');
            logoUrl = logoUrl.startsWith('/') 
              ? `${baseUrl}${logoUrl}` 
              : `${baseUrl}/${logoUrl}`;
          }

          // Debug: log final logo URL that will be fetched
          console.log('🖼 PDF Export - Logo URL used for PDF:', logoUrl);

          // Fetch logo and convert to base64
          const logoResponse = await fetch(logoUrl, {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            }
          });

          if (logoResponse.ok) {
            // Check if it's SVG first (by URL extension or content type)
            const isSvg = logoUrl.toLowerCase().endsWith('.svg');
            
            if (isSvg) {
              // Convert SVG to PNG for pdfMake compatibility
              // Read as text first for SVG
              const svgText = await logoResponse.text();
              // Debug: log the first part of the SVG (or error HTML) to see what we really got
              console.log('🖼 PDF Export - First 300 chars of SVG response:', svgText.slice(0, 300));

              companyLogoBase64 = await new Promise((resolve, reject) => {
                const img = new Image();
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                
                // Create a data URL from the SVG
                const svgBlob = new Blob([svgText], { type: 'image/svg+xml' });
                const url = URL.createObjectURL(svgBlob);
                
                img.onload = () => {
                  try {
                    // Set canvas size (max 300px width to keep PDF size reasonable)
                    const maxWidth = 300;
                    const scale = img.width > maxWidth ? maxWidth / img.width : 1;
                    canvas.width = img.width * scale;
                    canvas.height = img.height * scale;
                    
                    // Draw SVG to canvas
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    
                    // Convert canvas to base64 PNG
                    const pngDataUrl = canvas.toDataURL('image/png');
                    const base64data = pngDataUrl.split(',')[1];
                    
                    URL.revokeObjectURL(url);
                    resolve({
                      data: base64data,
                      mimeType: 'image/png'
                    });
                    console.log('✅ Company logo (SVG) converted to PNG for PDF export');
                  } catch (error) {
                    URL.revokeObjectURL(url);
                    reject(error);
                  }
                };
                
                img.onerror = () => {
                  URL.revokeObjectURL(url);
                  console.warn('⚠️ Could not render SVG logo for PDF, continuing without logo');
                  // Fail gracefully: resolve null so PDF generation can continue
                  resolve(null);
                };
                
                img.src = url;
              });
            } else {
              // For non-SVG images (PNG, JPG, etc.), use directly
              // Clone the response to read as blob
              const clonedResponse = logoResponse.clone();
              const logoBlob = await clonedResponse.blob();
              companyLogoBase64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                  const base64data = reader.result.split(',')[1]; // Remove data:type;base64, prefix
                  resolve({
                    data: base64data,
                    mimeType: logoBlob.type || 'image/png'
                  });
                };
                reader.onerror = reject;
                reader.readAsDataURL(logoBlob);
              });
              console.log('✅ Company logo loaded for PDF export');
            }
          } else {
            console.warn('⚠️ Failed to fetch logo file:', logoResponse.status);
          }
        }
      } else {
        console.warn('⚠️ Failed to fetch logo setting:', logoSettingResponse.status);
      }
    } catch (error) {
      console.warn('⚠️ Error fetching company logo for PDF:', error);
      // Logo is optional, continue without it
    }

    // Helper function to fetch file as base64
    const fetchFileAsBase64 = async (fileId, fileName) => {
      try {
        const token = localStorage.getItem('authToken');
        const userId = localStorage.getItem('userId');
        
        const response = await fetch(API_ENDPOINTS.fileAttachment.download(fileId), {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(userId ? { 'user-id': userId } : {})
          }
        });

        if (!response.ok) {
          if (response.status === 404) {
            console.warn(`File not found (may be deleted): ${fileName} (ID: ${fileId})`);
          } else {
            console.warn(`Failed to fetch file ${fileName} (ID: ${fileId}):`, response.status, response.statusText);
          }
          return null;
        }

        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64data = reader.result.split(',')[1]; // Remove data:type;base64, prefix
            resolve({
              data: base64data,
              mimeType: blob.type || 'application/octet-stream',
              fileName: fileName
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.warn(`Error fetching file ${fileName} (ID: ${fileId}):`, error.message || error);
        return null;
      }
    };

    // Fetch all file attachments as base64 if includeAttachments is true
    let fileAttachmentsData = [];
    if (includeAttachments && complaint.fileAttachments && complaint.fileAttachments.length > 0) {
      // Filter out deleted files and ensure we have valid file IDs
      const activeFiles = complaint.fileAttachments.filter(file => 
        file && 
        !file.isDeleted && 
        file.id && 
        file.fileName
      );
      console.log(`Fetching ${activeFiles.length} file attachments...`);
      
      for (const file of activeFiles) {
        const fileData = await fetchFileAsBase64(file.id, normalizeFileName(file.fileName || ''));
        if (fileData) {
          fileAttachmentsData.push({
            ...fileData,
            originalFile: file
          });
        } else {
          console.warn(`Skipping file ${file.fileName} (ID: ${file.id}) - could not be fetched`);
        }
      }
      console.log(`Successfully fetched ${fileAttachmentsData.length} of ${activeFiles.length} file attachments`);
      
      if (fileAttachmentsData.length < activeFiles.length) {
        missingFilesCount = activeFiles.length - fileAttachmentsData.length;
        console.warn(`${missingFilesCount} file(s) could not be included in the ZIP (may be deleted or missing on server)`);
      }
    }

    // Always fetch approval requests to ensure we have the latest data for PDF export
    let approvalRequests = [];
    
    // Check if approval requests are already in the complaint object (from backend service)
    if (complaint.approvalRequests && Array.isArray(complaint.approvalRequests) && complaint.approvalRequests.length > 0) {
      console.log('✅ Using approval requests from complaint object:', {
        count: complaint.approvalRequests.length,
        data: complaint.approvalRequests,
        firstApproval: complaint.approvalRequests[0]
      });
      approvalRequests = complaint.approvalRequests;
    } else {
      // Only fetch if not already present in complaint object
      console.log('⚠️ No approval requests in complaint object, fetching from API...');
    }
    
    // Always fetch fresh data to ensure we have the latest (even if we have some from complaint object)
    if (complaint.id) {
      try {
        const token = localStorage.getItem('authToken');
        const userId = localStorage.getItem('userId');
        
        // Entity type for complaints is "complaint", entityTypeId is 7
        const url = `${API_BASE_URL}/approval-requests/entity/complaint/${complaint.id}`;
        console.log('Fetching approval requests from:', url);
        
        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(userId ? { 'user-id': userId } : {})
          }
        });
        
        console.log('Approval requests response status:', response.status, response.ok);
        
        if (response.ok) {
          const fetchedData = await response.json();
          console.log('Raw fetched approval requests:', fetchedData);
          
          if (Array.isArray(fetchedData)) {
            approvalRequests = fetchedData;
            console.log('✅ Fetched approval requests for PDF:', {
              count: approvalRequests.length,
              data: approvalRequests,
              firstApproval: approvalRequests[0] || null
            });
          } else {
            console.warn('⚠️ Fetched data is not an array:', typeof fetchedData, fetchedData);
          }
        } else {
          const errorText = await response.text();
          console.warn('⚠️ Failed to fetch approval requests:', response.status, response.statusText, errorText);
        }
      } catch (error) {
        console.error('❌ Error fetching approval requests for PDF:', error);
      }
    }
    
    // Ensure approvalRequests is always an array
    if (!Array.isArray(approvalRequests)) {
      console.warn('⚠️ approvalRequests is not an array, setting to empty array');
      approvalRequests = [];
    }
    
    console.log('Final approval requests before merge:', {
      count: approvalRequests.length,
      isArray: Array.isArray(approvalRequests),
      data: approvalRequests
    });

    // Merge approval requests into complaint object
    const complaintWithApprovals = {
      ...complaint,
      approvalRequests: approvalRequests || []
    };

    // Debug: Log approval requests data
    console.log('PDF Export - Approval Requests:', {
      hasApprovalRequests: !!complaintWithApprovals.approvalRequests,
      isArray: Array.isArray(complaintWithApprovals.approvalRequests),
      length: complaintWithApprovals.approvalRequests?.length,
      data: complaintWithApprovals.approvalRequests
    });

    // Helper functions
    const formatDate = (date) => {
      if (!date) return 'N/A';
      try {
        return dayjs(date).format('DD/MM/YYYY HH:mm');
      } catch {
        return date;
      }
    };

    const formatDateOnly = (date) => {
      if (!date) return 'N/A';
      try {
        return dayjs(date).format('DD/MM/YYYY');
      } catch {
        return date;
      }
    };

    const formatBoolean = (value) => {
      if (value === true) return 'Yes';
      if (value === false) return 'No';
      return 'N/A';
    };

    const formatArray = (arr, field = 'name') => {
      if (!arr || !Array.isArray(arr) || arr.length === 0) return 'N/A';
      return arr.map(item => item[field] || item).join(', ');
    };

    // Use complaint with approvals
    const complaintData = complaintWithApprovals;

    // Prefer snapshot stored at creation; otherwise fetch live by complaint type
    let referenceDocument = null;
    let referenceDocumentText = complaintData.referenceDocument || null;
    try {
      if (!referenceDocumentText && (complaintData.complaintTypeId || complaintData.complaintType)) {
        const token = localStorage.getItem('authToken');
        
        // Get complaint type ID and name
        let complaintTypeId = complaintData.complaintTypeId;
        let complaintTypeName = complaintData.complaintType?.name;

        if (typeof complaintTypeId === 'object' && complaintTypeId !== null) {
          complaintTypeId = complaintTypeId.id || complaintTypeId.value;
          complaintTypeName = complaintTypeName || complaintTypeId.name;
        }

        // If we don't have the complaint type name, fetch it
        if (!complaintTypeName && complaintTypeId) {
          const complaintTypeResponse = await fetch(`${API_BASE_URL}/complaint-type/${complaintTypeId}`, {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            }
          });
          
          if (complaintTypeResponse.ok) {
            const complaintType = await complaintTypeResponse.json();
            complaintTypeName = complaintType.name;
          }
        }

        if (complaintTypeName) {
          // Map complaint type names to expected module name patterns
          const getExpectedModuleNameForType = (name) => {
            const mapping = {
              'GDP': 'COMPLAINT_GDP',
              'GMP': 'COMPLAINT_GMP'
            };
            return mapping[name?.toUpperCase()] || null;
          };

          const expectedModuleName = getExpectedModuleNameForType(complaintTypeName);
          
          if (expectedModuleName) {
            // Fetch all document modules
            const modulesResponse = await fetch(`${API_BASE_URL}/document-module`, {
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {})
              }
            });

            if (modulesResponse.ok) {
              const allDocumentModules = await modulesResponse.json();
              const module = allDocumentModules.find(m => 
                m.name === expectedModuleName || 
                m.name?.toUpperCase() === expectedModuleName.toUpperCase() ||
                (m.name?.includes('GDP') && complaintTypeName.toUpperCase() === 'GDP') ||
                (m.name?.includes('GMP') && complaintTypeName.toUpperCase() === 'GMP')
              );

              if (module) {
                // Fetch documents for this module
                const documentsResponse = await fetch(`${API_BASE_URL}/document-management/document?module=${encodeURIComponent(module.name)}`, {
                  headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {})
                  }
                });

                if (documentsResponse.ok) {
                  const documents = await documentsResponse.json();
                  const docsArray = Array.isArray(documents) ? documents : [];
                  if (docsArray.length > 0) {
                    referenceDocument = docsArray[0];
                    referenceDocumentText =
                      `${referenceDocument.code || ''}${referenceDocument.currentRevision?.version ? ` - ${referenceDocument.currentRevision.version}` : ''}`.trim() ||
                      null;
                    console.log('📄 PDF Export - Reference document found:', referenceDocument.code);
                  }
                }
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('📄 PDF Export - Error fetching reference document:', error);
    }

    // Get form sections from config
    const complaintTypes = complaintData.complaintType ? [complaintData.complaintType] : [];
    const formSections = complaintConfig.getFormSectionsByType(
      complaintData.complaintTypeId,
      complaintTypes
    );

    const complaintTypeName = complaintData.complaintType?.name?.toUpperCase() || '';
    const isGDP = complaintTypeName === 'GDP';
    const isGMP = complaintTypeName === 'GMP';

    // Extract code parts (for future use if needed)
    // const extractCodeParts = (code) => {
    //   if (!code) return { year: 'N/A', number: 'N/A' };
    //   const match = code.match(/(\d{4})[-\s]*(\d+)/);
    //   if (match) {
    //     return { year: match[1], number: match[2] };
    //   }
    //   return { year: dayjs().format('YYYY'), number: code };
    // };

    // Section renderers based on form sections
    const renderSection = (section) => {
      const title = section.title || '';
      
      // Field value getter
      const getFieldValue = (fieldName) => {
        const field = complaintConfig.fields.find(f => f.name === fieldName);
        if (!field) return null;

        const value = complaintData[fieldName];
        if (value === null || value === undefined) return null;

        // Handle relations
        if (field.lookupEndpoint) {
          const relationName = field.displayField?.split('.')[0] || field.name.replace('Id', '');
          const relation = complaintData[relationName];
          if (relation) {
            if (Array.isArray(relation)) {
              return relation.map(r => r.name || r.userDisplayName || r).join(', ');
            }
            return relation.name || relation.userDisplayName || relation.code || relation.title || value;
          }
        }

        // Handle arrays
        if (Array.isArray(value)) {
          return value.map(v => v.name || v.userDisplayName || v).join(', ');
        }

        // Handle dates
        if (value instanceof Date || (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/))) {
          return formatDateOnly(value);
        }

        // Handle booleans
        if (typeof value === 'boolean') {
          return formatBoolean(value);
        }

        return value;
      };

      // Render field
      const renderField = (fieldName, label, alwaysShow = false) => {
        const value = getFieldValue(fieldName);
        // If alwaysShow is true, always render the field (even if null/undefined)
        if (alwaysShow) {
          return {
            columns: [
              { text: label, style: 'fieldLabel', width: '35%' },
              { text: value !== null && value !== undefined ? String(value) : 'N/A', style: 'fieldValue', width: '65%' }
            ],
            margin: [0, 0, 0, 8]
          };
        }
        // Otherwise, only render if value exists
        if (value === null || value === undefined || value === 'N/A') return null;
        return {
          columns: [
            { text: label, style: 'fieldLabel', width: '35%' },
            { text: String(value), style: 'fieldValue', width: '65%' }
          ],
          margin: [0, 0, 0, 8]
        };
      };

      // Section-specific rendering
      const sections = {
        'Basic Information': () => [
          renderField('code', 'Complaint Code'),
          renderField('title', 'Title'),
          renderField('description', 'Description'),
          renderField('complaintTypeId', 'Complaint Type'),
          renderField('priorityId', 'Priority'),
          renderField('statusId', 'Status')
        ].filter(Boolean),

        'Source / Depositor Information': () => [
          renderField('sourceId', 'Source'),
          renderField('sourceDetails', 'Source Details'),
          renderField('customerId', 'Customer'),
          renderField('customerReportedBy', 'Reported By'),
          renderField('sampleAvailableId', 'Sample Available'),
          renderField('reportedAt', 'Reported At')
        ].filter(Boolean),

        'Products': () => {
          const products = Array.isArray(complaintData.products) ? complaintData.products : [];
          if (products.length === 0) {
            return [
              {
                columns: [
                  { text: 'Products', style: 'fieldLabel', width: '35%' },
                  { text: 'N/A', style: 'fieldValue', width: '65%' }
                ],
                margin: [0, 0, 0, 8]
              }
            ];
          }
          return [
            {
              table: {
                headerRows: 1,
                widths: ['*', '*', '*', 'auto', 'auto'],
                body: [
                  [
                    { text: 'Code', style: 'tableHeader' },
                    { text: 'Name', style: 'tableHeader' },
                    { text: 'Batch', style: 'tableHeader' },
                    { text: 'Qty', style: 'tableHeader' },
                    { text: 'Expiry', style: 'tableHeader' },
                  ],
                  ...products.map((p) => [
                    { text: p.productCode || 'N/A', style: 'tableCell' },
                    { text: p.productName || 'N/A', style: 'tableCell' },
                    { text: p.batchNumber || 'N/A', style: 'tableCell' },
                    { text: p.productQuantity != null ? String(p.productQuantity) : 'N/A', style: 'tableCell' },
                    { text: formatDateOnly(p.expiryDate), style: 'tableCell' },
                  ])
                ]
              },
              layout: 'lightHorizontalLines',
              margin: [0, 5, 0, 0]
            }
          ];
        },

        'Complaint Category': () => {
          const fields = [
            renderField('complaintCategoryNonMedicalId', 'Non-Medical Category'),
            renderField('complaintCategoryNonMedicalOther', 'Non-Medical Other'),
            renderField('complaintCategoryMedical', 'Medical Category'),
            renderField('recordedById', 'Recorded By', true),
            renderField('recordedAt', 'Recorded At', true)
          ];
          return fields.filter(Boolean);
        },

        'Investigation': () => {
          const fields = [
            renderField('investigationStartDate', 'Investigation Start Date'),
            renderField('investigationCategoryId', 'Investigation Category')
          ];

          if (isGDP) {
            fields.push(renderField('sampleInspectionId', 'Sample Inspection'));
            fields.push({
              columns: [
                { text: 'Forward To', style: 'fieldLabel', width: '35%' },
                { text: formatArray(complaint.forwardTo), style: 'fieldValue', width: '65%' }
              ],
              margin: [0, 0, 0, 8]
            });
          } else if (isGMP) {
            fields.push({
              columns: [
                { text: 'Involved Departments', style: 'fieldLabel', width: '35%' },
                { text: formatArray(complaint.involvedDepartments), style: 'fieldValue', width: '65%' }
              ],
              margin: [0, 0, 0, 8]
            });
          }

          return fields.filter(Boolean);
        },

        'Actions Taken': () => {
          if (!isGMP || !complaintData.complaintActionTaken || complaintData.complaintActionTaken.length === 0) {
            return null;
          }
          return [
            {
              table: {
                headerRows: 1,
                widths: ['*', '*', '*'],
                body: [
                  [
                    { text: 'Type', style: 'tableHeader' },
                    { text: 'Description', style: 'tableHeader' },
                    { text: 'Started', style: 'tableHeader' }
                  ],
                  ...complaintData.complaintActionTaken.map(action => [
                    { text: action.actionTaken?.name || 'N/A', style: 'tableCell' },
                    { text: action.description || 'N/A', style: 'tableCell' },
                    { text: formatDate(action.startedAt), style: 'tableCell' }
                  ])
                ]
              },
              layout: 'lightHorizontalLines',
              margin: [0, 5, 0, 0]
            }
          ];
        },

        'Conclusion': () => {
          const fields = [
            renderField('findingTypeId', 'Finding Type')
          ];

          if (isGDP) {
            fields.push({
              columns: [
                { text: 'Involved Departments', style: 'fieldLabel', width: '35%' },
                { text: formatArray(complaintData.involvedDepartments), style: 'fieldValue', width: '65%' }
              ],
              margin: [0, 0, 0, 8]
            });
          }

          return fields.filter(Boolean);
        },

        'Possible Causes': () => [
          {
            table: {
              headerRows: 1,
              widths: ['*', 'auto', '*'],
              body: [
                [
                  { text: 'Cause', style: 'tableHeader' },
                  { text: 'Identified', style: 'tableHeader' },
                  { text: 'Notes', style: 'tableHeader' }
                ],
                [
                  { text: 'Human Factor', style: 'tableCell' },
                  { text: formatBoolean(complaintData.humanFactor), style: 'tableCell', alignment: 'center' },
                  { text: complaintData.humanFactorNote || 'N/A', style: 'tableCell' }
                ],
                [
                  { text: 'Equipment', style: 'tableCell' },
                  { text: formatBoolean(complaintData.equipment), style: 'tableCell', alignment: 'center' },
                  { text: complaintData.equipmentNote || 'N/A', style: 'tableCell' }
                ],
                [
                  { text: 'Methods', style: 'tableCell' },
                  { text: formatBoolean(complaintData.methods), style: 'tableCell', alignment: 'center' },
                  { text: complaintData.methodsNote || 'N/A', style: 'tableCell' }
                ],
                [
                  { text: 'Materials', style: 'tableCell' },
                  { text: formatBoolean(complaintData.materials), style: 'tableCell', alignment: 'center' },
                  { text: complaintData.materialsNote || 'N/A', style: 'tableCell' }
                ],
                [
                  { text: 'Supplier', style: 'tableCell' },
                  { text: formatBoolean(complaintData.supplier), style: 'tableCell', alignment: 'center' },
                  { text: complaintData.supplierNote || 'N/A', style: 'tableCell' }
                ]
              ]
            },
            layout: 'lightHorizontalLines',
            margin: [0, 5, 0, 0]
          }
        ],

        'Corrective / Preventive Actions': () => {
          if (!complaintData.complaintActions || complaintData.complaintActions.length === 0) {
            return null;
          }
          return [
            {
              table: {
                headerRows: 1,
                widths: [50, '*', 80, 80, 70, 70],
                body: [
                  [
                    { text: 'Type', style: 'tableHeader' },
                    { text: 'Description', style: 'tableHeader' },
                    { text: 'Responsible', style: 'tableHeader' },
                    { text: 'Started', style: 'tableHeader' },
                    { text: 'Due Date', style: 'tableHeader' },
                    { text: 'Close Date', style: 'tableHeader' }
                  ],
                  ...complaintData.complaintActions.map(action => [
                    { text: action.actionType?.name || 'N/A', style: 'tableCell', fontSize: 9 },
                    { text: action.description || 'N/A', style: 'tableCell' },
                    { text: action.responsible || 'N/A', style: 'tableCell', fontSize: 9 },
                    { text: formatDate(action.startedAt), style: 'tableCell', fontSize: 9 },
                    { text: formatDateOnly(action.dueDate), style: 'tableCell', fontSize: 9 },
                    { text: formatDateOnly(action.closeDate), style: 'tableCell', fontSize: 9 }
                  ])
                ]
              },
              layout: 'lightHorizontalLines',
              margin: [0, 5, 0, 0]
            }
          ];
        },

        'Links': () => {
          const fields = [
            renderField('riskAssessmentId', 'Risk Assessment', true),
            renderField('deviationId', 'Deviation', true)
          ];
          // Always return at least one field, even if all are null
          return fields.length > 0 ? fields : [
            {
              columns: [
                { text: 'Risk Assessment', style: 'fieldLabel', width: '35%' },
                { text: 'N/A', style: 'fieldValue', width: '65%' }
              ],
              margin: [0, 0, 0, 8]
            },
            {
              columns: [
                { text: 'Deviation', style: 'fieldLabel', width: '35%' },
                { text: 'N/A', style: 'fieldValue', width: '65%' }
              ],
              margin: [0, 0, 0, 8]
            }
          ];
        },

        'Timeline': () => {
          const fields = [
            renderField('dueDate', 'Due Date', true),
            renderField('resolvedAt', 'Resolved Date', true),
            renderField('closedAt', 'Closed Date', true),
            renderField('closedById', 'Closed By', true)
          ];
          // Always return fields
          return fields.length > 0 ? fields : [
            {
              columns: [
                { text: 'Due Date', style: 'fieldLabel', width: '35%' },
                { text: 'N/A', style: 'fieldValue', width: '65%' }
              ],
              margin: [0, 0, 0, 8]
            }
          ];
        },

        'Completion – Observations': () => {
          const fields = [
            renderField('completionComments', 'Completion Comments', true)
          ];
          // Always return at least one field
          return fields.length > 0 ? fields : [
            {
              columns: [
                { text: 'Completion Comments', style: 'fieldLabel', width: '35%' },
                { text: 'N/A', style: 'fieldValue', width: '65%' }
              ],
              margin: [0, 0, 0, 8]
            }
          ];
        },

        'File Attachments': () => {
          if (!complaintData.fileAttachments || complaintData.fileAttachments.length === 0) {
            return [
              {
                columns: [
                  { text: 'File Attachments', style: 'fieldLabel', width: '35%' },
                  { text: 'No attachments', style: 'fieldValue', width: '65%' }
                ],
                margin: [0, 0, 0, 8]
              }
            ];
          }
          
          const activeFiles = complaintData.fileAttachments.filter(file => !file.isDeleted);
          return [
            {
              table: {
                headerRows: 1,
                widths: ['*', 'auto', 'auto'],
                body: [
                  [
                    { text: 'File Name', style: 'tableHeader' },
                    { text: 'Size', style: 'tableHeader' },
                    { text: 'Uploaded', style: 'tableHeader' }
                  ],
                  ...activeFiles.map(file => [
                    { text: normalizeFileName(file.fileName || file.name || 'N/A'), style: 'tableCell' },
                    { text: file.fileSize ? `${(file.fileSize / 1024).toFixed(2)} KB` : 'N/A', style: 'tableCell' },
                    { text: formatDate(file.uploadedAt || file.createdAt), style: 'tableCell' }
                  ])
                ]
              },
              layout: 'lightHorizontalLines',
              margin: [0, 5, 0, 0]
            }
          ];
        }
      };

      const renderer = sections[title];
      if (!renderer) {
        // Log missing renderer for debugging
        console.warn(`No renderer found for section: "${title}"`);
        return null;
      }

      const content = renderer();
      if (!content || content.length === 0) {
        // Log empty content for debugging
        console.warn(`Empty content for section: "${title}"`);
        return null;
      }

      return {
        stack: [
          {
            text: title.toUpperCase(),
            style: 'sectionTitle',
            margin: [0, 0, 0, 12]
          },
          ...content
        ],
        margin: [0, 0, 0, 20],
        pageBreak: 'avoid'
        // Removed unbreakable: true to prevent empty pages at the end
      };
    };

    // Build content array
    const headerContent = [];
    
    // Add big logo on first page if available
    if (companyLogoBase64) {
      headerContent.push({
        columns: [
          {
            image: logoImageKey,
            width: 200,
            alignment: 'left',
            margin: [0, 0, 0, 20]
          },
          {
            stack: [
              {
                text: 'COMPLAINT REPORT',
                style: 'reportTitle',
                alignment: 'right',
                margin: [0, 0, 0, 5]
              },
              {
                text: referenceDocumentText || complaintData.code || 'N/A',
                style: 'reportSubtitle',
                alignment: 'right',
                margin: [0, 0, 0, 0]
              }
            ],
            alignment: 'right',
            width: '*'
          }
        ],
        margin: [0, 0, 0, 20]
      });
    } else {
      // No logo, center the title
      headerContent.push(
        {
          text: 'COMPLAINT REPORT',
          style: 'reportTitle',
          alignment: 'center',
          margin: [0, 0, 0, 20]
        },
        {
          text: referenceDocumentText || complaintData.code || 'N/A',
          style: 'reportSubtitle',
          alignment: 'center',
          margin: [0, 0, 0, 20]
        }
      );
    }
    
    // Add separator line
    headerContent.push({
      canvas: [
        {
          type: 'line',
          x1: 0,
          y1: 0,
          x2: 515,
          y2: 0,
          lineWidth: 1,
          lineColor: '#cccccc'
        }
      ],
      margin: [0, 0, 0, 20]
    });

    const content = [
      // Header
      {
        stack: headerContent
      },
      // Render all sections in config order
      ...formSections.map(section => renderSection(section)).filter(Boolean),
      // Review and Approval Status
      // Always show this section if approvalRequests exists (even if empty array, to show pending status)
      ...(function() {
        console.log('🔍 Before rendering approval requests:', {
          hasApprovalRequests: !!complaintData.approvalRequests,
          isArray: Array.isArray(complaintData.approvalRequests),
          length: complaintData.approvalRequests?.length,
          data: complaintData.approvalRequests,
          firstApproval: complaintData.approvalRequests?.[0]
        });
        
        if (!complaintData.approvalRequests || !Array.isArray(complaintData.approvalRequests)) {
          console.log('⚠️ No approval requests to render');
          return [];
        }
        
        // Get only the last (most recent) approval request
        let lastApproval = null;
        if (complaintData.approvalRequests && complaintData.approvalRequests.length > 0) {
          // Sort by requestedAt descending (most recent first) and get the first one
          const sortedApprovals = [...complaintData.approvalRequests].sort((a, b) => {
            const dateA = new Date(a.requestedAt || a.createdAt || '1970-01-01');
            const dateB = new Date(b.requestedAt || b.createdAt || '1970-01-01');
            return dateB.getTime() - dateA.getTime(); // Descending order
          });
          lastApproval = sortedApprovals[0];
          console.log('✅ Building approval requests section - showing only last approval:', {
            totalApprovals: complaintData.approvalRequests.length,
            selectedApproval: {
              code: lastApproval?.code,
              title: lastApproval?.title,
              requestedAt: lastApproval?.requestedAt,
              status: lastApproval?.status?.name
            }
          });
        } else {
          console.log('⚠️ No approval requests to render');
        }
        
        const approvalSectionContent = lastApproval 
          ? (() => {
              const approval = lastApproval;
              console.log(`📄 Processing last approval:`, {
                code: approval.code,
                title: approval.title,
                hasSteps: !!approval.steps,
                stepsCount: approval.steps?.length || 0
              });
              
              return [
              {
                text: `Approval Request: ${approval.code || approval.id || 'N/A'}`,
                style: 'subsectionTitle',
                margin: [0, 0, 0, 8]
              },
              {
                columns: [
                  { text: 'Status:', style: 'fieldLabel', width: '35%' },
                  { text: approval.status?.name || 'N/A', style: 'fieldValue', width: '65%' }
                ],
                margin: [0, 0, 0, 5]
              },
              {
                columns: [
                  { text: 'Requested By:', style: 'fieldLabel', width: '35%' },
                  { text: approval.requestedBy?.userDisplayName || approval.requestedBy?.name || 'N/A', style: 'fieldValue', width: '65%' }
                ],
                margin: [0, 0, 0, 5]
              },
              {
                columns: [
                  { text: 'Requested Date:', style: 'fieldLabel', width: '35%' },
                  { text: formatDate(approval.requestedAt), style: 'fieldValue', width: '65%' }
                ],
                margin: [0, 0, 0, 5]
              },
              {
                columns: [
                  { text: 'Due Date:', style: 'fieldLabel', width: '35%' },
                  { text: formatDateOnly(approval.dueDate), style: 'fieldValue', width: '65%' }
                ],
                margin: [0, 0, 0, 5]
              },
              {
                columns: [
                  { text: 'Title:', style: 'fieldLabel', width: '35%' },
                  { text: approval.title || 'N/A', style: 'fieldValue', width: '65%' }
                ],
                margin: [0, 0, 0, 5]
              },
              {
                columns: [
                  { text: 'Description:', style: 'fieldLabel', width: '35%' },
                  { text: approval.description || 'N/A', style: 'fieldValue', width: '65%' }
                ],
                margin: [0, 0, 0, 10]
              },
              ...(approval.steps && approval.steps.length > 0 ? [
                {
                  text: 'Approval Steps:',
                  style: 'subsectionTitle',
                  margin: [0, 0, 0, 8]
                },
                {
                  table: {
                    headerRows: 1,
                    widths: ['auto', '*', '*', '*', 'auto', '*'],
                    body: [
                      [
                        { text: 'Step', style: 'tableHeader', alignment: 'center' },
                        { text: 'Step Name', style: 'tableHeader' },
                        { text: 'Type', style: 'tableHeader' },
                        { text: 'Status', style: 'tableHeader' },
                        { text: 'Approved Date', style: 'tableHeader' },
                        { text: 'Approver/Reviewer', style: 'tableHeader' }
                      ],
                      ...approval.steps.map(step => [
                        { text: step.stepNumber?.toString() || 'N/A', style: 'tableCell', alignment: 'center' },
                        { text: step.stepName || 'N/A', style: 'tableCell' },
                        { text: step.stepType || 'N/A', style: 'tableCell' },
                        { text: step.status?.name || 'Pending', style: 'tableCell' },
                        { text: step.approvedAt ? formatDate(step.approvedAt) : (step.completedAt ? formatDate(step.completedAt) : 'N/A'), style: 'tableCell' },
                        {
                          text: [
                            step.approvedBy?.userDisplayName || step.approvedBy?.name,
                            step.reviewedBy?.userDisplayName || step.reviewedBy?.name,
                            step.assignedApprover?.userDisplayName || step.assignedApprover?.name
                          ].filter(Boolean).join(' / ') || 'N/A',
                          style: 'tableCell'
                        }
                      ])
                    ]
                  },
                  layout: 'lightHorizontalLines',
                  margin: [0, 5, 0, 10]
                }
              ] : [])
            ];
          })()
          : [
            // Show message if approvalRequests exists but is empty
            {
              columns: [
                { text: 'Status:', style: 'fieldLabel', width: '35%' },
                { text: 'No approval requests found', style: 'fieldValue', width: '65%' }
              ],
              margin: [0, 0, 0, 8]
            }
          ];
        
        console.log('📋 Approval section content items:', approvalSectionContent.length);
        console.log('📋 Approval section content structure:', {
          isArray: Array.isArray(approvalSectionContent),
          length: approvalSectionContent.length,
          firstItem: approvalSectionContent[0] || null
        });
        
        // approvalSectionContent is already a flat array (single approval), so no need to flatten
        const flattenedContent = approvalSectionContent;
        console.log('📋 Flattened approval content items:', flattenedContent.length);
        console.log('📋 First few flattened items:', flattenedContent.slice(0, 3).map(item => ({
          hasText: !!item.text,
          hasColumns: !!item.columns,
          hasTable: !!item.table,
          text: item.text?.substring(0, 50) || 'N/A'
        })));
        
        return [
        {
          stack: [
            {
              text: 'REVIEW AND APPROVAL STATUS',
              style: 'sectionTitle',
              margin: [0, 0, 0, 12]
            },
            ...flattenedContent
          ],
          margin: [0, 0, 0, 20],
          pageBreak: 'avoid'
        }
      ];
      }())
    ];
    
    // Debug: Log final content array structure
    console.log('📋 Final content array:', {
      totalSections: content.length,
      hasApprovalSection: content.some(item => 
        item.stack && 
        Array.isArray(item.stack) && 
        item.stack.some(s => s && s.text === 'REVIEW AND APPROVAL STATUS')
      ),
      contentTypes: content.map(item => {
        if (item.stack) return 'stack';
        if (item.text) return 'text';
        if (item.table) return 'table';
        return 'other';
      })
    });

    // Prepare images dictionary (validate logo before passing to pdfMake)
    let images = {};
    if (companyLogoBase64 && companyLogoBase64.data) {
      const mimeType = companyLogoBase64.mimeType || 'image/png';
      const logoDataUrl = `data:${mimeType};base64,${companyLogoBase64.data}`;

      // Basic validation to avoid pdfMake "Unknown image format" errors
      const isValidImageDataUrl = /^data:image\/(png|jpe?g);base64,/i.test(logoDataUrl);
      console.log('🖼 PDF Export - Logo data URL prefix:', logoDataUrl.slice(0, 50), 'valid:', isValidImageDataUrl);

      if (isValidImageDataUrl) {
        images[logoImageKey] = logoDataUrl;
      } else {
        console.warn('⚠️ PDF Export - Invalid logo data URL detected, skipping logo image');
      }
    }

    // PDF Document Definition
    const docDefinition = {
      pageSize: 'A4',
      // Adjust bottom margin to account for footer (60mm top, 50mm bottom to leave space for footer)
      pageMargins: [40, 60, 40, 50],
      defaultStyle: {
        font: 'Roboto',
        fontSize: 10,
        lineHeight: 1.4,
        color: '#333333'
      },
      header: function(currentPage, pageCount, pageSize) {
        // Add logo to top left of every page except the first page (first page has big logo in content)
        if (companyLogoBase64 && currentPage > 1) {
          return {
            image: logoImageKey,
            width: 120,
            alignment: 'left',
            margin: [40, 20, 0, 0]
          };
        }
        return null;
      },
      footer: function(currentPage, pageCount) {
        // Always show footer - pdfMake handles page count correctly
        return {
          text: `Page ${currentPage} of ${pageCount} | Generated: ${dayjs().format('DD/MM/YYYY HH:mm')}`,
          alignment: 'center',
          fontSize: 8,
          color: '#999999',
          margin: [0, 10, 0, 0]
        };
      },
      content: content,
      // Register images for pdfMake
      images,
      styles: {
        reportTitle: {
          fontSize: 18,
          bold: true,
          color: '#1a1a1a'
        },
        reportSubtitle: {
          fontSize: 12,
          color: '#666666'
        },
        sectionTitle: {
          fontSize: 14,
          bold: true,
          color: '#2c3e50',
          decoration: 'underline'
        },
        subsectionTitle: {
          fontSize: 12,
          bold: true,
          color: '#34495e'
        },
        fieldLabel: {
          fontSize: 10,
          bold: true,
          color: '#555555'
        },
        fieldValue: {
          fontSize: 10,
          color: '#333333'
        },
        tableHeader: {
          fontSize: 9,
          bold: true,
          color: '#ffffff',
          fillColor: '#34495e'
        },
        tableCell: {
          fontSize: 9,
          color: '#333333'
        }
      }
    };

    // Generate PDF
    const pdfDoc = pdfMake.default.createPdf(docDefinition);
    
    // Store result to return
    const result = { missingFilesCount };
    
    if (includeAttachments && fileAttachmentsData.length > 0) {
      // Create a ZIP file containing the PDF and all attachments
      try {
        // Import JSZip
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();
        
        // Generate PDF as blob and add to ZIP
        pdfDoc.getBlob((pdfBlob) => {
          // Add PDF to ZIP
          zip.file(`Complaint-${complaintData.code || complaintData.id}.pdf`, pdfBlob);
          
          // Add all attachment files to ZIP
          fileAttachmentsData.forEach((fileData) => {
            const { data, fileName } = fileData;
            // Convert base64 to binary
            const binaryString = atob(data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            zip.file(fileName, bytes);
          });
          
          // Generate and download ZIP file
          zip.generateAsync({ type: 'blob' }).then((zipBlob) => {
            const url = window.URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Complaint-${complaintData.code || complaintData.id}-with-attachments.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }).catch((error) => {
            console.error('Error generating ZIP file:', error);
            // Fallback to just downloading the PDF
            pdfDoc.download(`Complaint-${complaintData.code || complaintData.id}.pdf`);
          });
        });
      } catch (error) {
        console.error('Error importing JSZip or creating ZIP:', error);
        // Fallback to just downloading the PDF
        pdfDoc.download(`Complaint-${complaintData.code || complaintData.id}.pdf`);
      }
    } else {
      // Just download the PDF
      pdfDoc.download(`Complaint-${complaintData.code || complaintData.id}.pdf`);
    }
    
    // Return info about missing files for user notification
    return result;
    
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF. Please try again.');
  }
};

